import type { PlayerNameFields, UserPreferences } from '../types';

export const defaultUserPreferences: UserPreferences = {
  playerNameDisplayFormat: 'first_last',
  playerSortOrder: 'last_name',
  groupPlayersBySex: false,
};

export function formatPlayerName(player: PlayerNameFields | undefined, preferences: UserPreferences): string {
  if (!player) return '';
  const firstName = player.first_name || '';
  const lastName = player.last_name || '';
  if (preferences.playerNameDisplayFormat === 'last_first') {
    if (firstName && lastName) return `${lastName}, ${firstName}`;
    return lastName || firstName;
  }
  return [firstName, lastName].filter(Boolean).join(' ');
}

export function formatPlayerFirstLast(player: PlayerNameFields | undefined): string {
  if (!player) return '';
  return [player.first_name || '', player.last_name || ''].filter(Boolean).join(' ');
}

export function formatPlayerFirstLastInitial(player: PlayerNameFields | undefined): string {
  if (!player) return '';
  const firstName = player.first_name || '';
  const lastInitial = (player.last_name || '').trim().charAt(0);
  if (firstName && lastInitial) return `${firstName} ${lastInitial}.`;
  return firstName || (player.last_name || '');
}

export function comparePlayerNames(a: PlayerNameFields, b: PlayerNameFields, preferences: UserPreferences): number {
  const firstCompare = (a.first_name || '').localeCompare(b.first_name || '');
  const lastCompare = (a.last_name || '').localeCompare(b.last_name || '');
  const nameCompare = preferences.playerSortOrder === 'first_name'
    ? firstCompare || lastCompare
    : lastCompare || firstCompare;
  if (preferences.groupPlayersBySex) {
    return (a.sex || '').localeCompare(b.sex || '') || nameCompare;
  }
  return nameCompare;
}

export function loadUserPreferences(): UserPreferences {
  try {
    const saved = window.localStorage.getItem('courtcaptain:user-preferences');
    if (!saved) return defaultUserPreferences;
    const parsed = JSON.parse(saved) as Partial<UserPreferences>;
    const rawSortOrder = (parsed as { playerSortOrder?: string }).playerSortOrder;
    const migratedSortOrder = rawSortOrder === 'sex_then_name' ? 'last_name' : rawSortOrder;
    return {
      ...defaultUserPreferences,
      ...parsed,
      playerSortOrder: migratedSortOrder === 'first_name' ? 'first_name' : 'last_name',
      groupPlayersBySex: parsed.groupPlayersBySex ?? rawSortOrder === 'sex_then_name',
    } as UserPreferences;
  } catch {
    return defaultUserPreferences;
  }
}

export function saveUserPreferences(preferences: UserPreferences): void {
  window.localStorage.setItem('courtcaptain:user-preferences', JSON.stringify(preferences));
}
